import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { X, Shield } from 'lucide-react';

interface AdminLoginPopupProps {
  onClose: () => void;
}

const AdminLoginPopup: React.FC<AdminLoginPopupProps> = ({ onClose }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAdmin();
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handlePinChange = (index: number, value: string) => {
    if (value.length > 1) return; // Sadece tek karakter kabul et
    if (!/^\d*$/.test(value)) return; // Sadece rakam kabul et

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Sonraki kutucuğa geç
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // PIN tamamlandığında otomatik giriş yap
    if (newPin.every(digit => digit !== '') && newPin.join('').length === 6) {
      handleAutoLogin(newPin.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      // Boş kutucukta backspace basıldığında önceki kutucuğa geç
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleAutoLogin = async (pinValue: string) => {
    setIsLoading(true);
    
    // Kısa bir gecikme ekle (kullanıcı deneyimi için)
    setTimeout(() => {
      const success = login(pinValue);
      if (success) {
        navigate('/admin');
        onClose();
      } else {
        setError('Yanlış PIN kodu');
        setPin(['', '', '', '', '', '']);
        setIsLoading(false);
        // İlk kutucuğa odaklan
        inputRefs.current[0]?.focus();
      }
    }, 500);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newPin = pastedData.split('');
      setPin(newPin);
      setError('');
      handleAutoLogin(pastedData);
    }
  };

  // İlk kutucuğa otomatik odaklan
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 rounded-xl mr-3">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Admin Girişi</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-center text-gray-600 mb-6">6 haneli PIN kodunuzu girin</p>
          
          <div className="mb-6">
            <div className="flex justify-center gap-3 mb-4">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-10 h-10 text-center text-lg font-bold border-2 rounded-lg focus:outline-none transition-all ${
                    error 
                      ? 'border-red-300 bg-red-50 focus:border-red-500' 
                      : 'border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200'
                  } ${digit ? 'bg-emerald-50 border-emerald-300' : ''}`}
                  disabled={isLoading}
                />
              ))}
            </div>
            
            {error && (
              <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-200 mb-4">
                {error}
              </div>
            )}
            
            {isLoading && (
              <div className="flex items-center justify-center text-emerald-600 mb-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mr-2"></div>
                <span className="text-sm font-medium">Giriş yapılıyor...</span>
              </div>
            )}
            
            {/* PIN gösterge çubukları */}
            <div className="flex justify-center gap-1">
              {pin.map((digit, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    digit ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            PIN kodunuzu tek seferde yapıştırabilir veya tek tek girebilirsiniz
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPopup;